/**
 * Where a dropdown panel goes when it is portalled out of the page.
 *
 * WHY PORTAL AT ALL. An absolutely-positioned panel is at the mercy of every
 * ancestor it happens to have. Two independent things kill it, and both are
 * ordinary layout:
 *
 *   - `overflow-hidden` / `overflow-clip` CROPS it, at any z-index. The
 *     artifacts shelf rounds its group corners that way; the brainstorm room's
 *     card clips for the same kind of reason.
 *   - a later sibling with an equal or higher z-index paints OVER it. The
 *     brainstorm run bar is `sticky z-30` and comes after the header, so a
 *     `z-30` panel opened underneath the page.
 *
 * Raising the z-index answers the second and does nothing about the first, and
 * only until the next sticky header lands. Leaving the tree answers both, for
 * good — at the price of computing coordinates, which is what lives here.
 *
 * Pure and DOM-free (it takes a plain rect and the viewport size), so CRA's jest
 * can test the flipping and clamping without laying anything out.
 */

/** Breathing room between the anchor and the panel. */
export const PANEL_GAP = 8;
/** Minimum distance kept from the viewport edge. */
export const PANEL_EDGE = 8;
/** Below this much room underneath, dropping down is not worth it. */
const MIN_DROP = 260;

export interface AnchorRect {
  top: number;
  bottom: number;
  right: number;
}

export interface AnchoredPos {
  left: number;
  /** Exactly one of top/bottom is set: top when it drops, bottom when it flips. */
  top?: number;
  bottom?: number;
  maxHeight: number;
}

/**
 * Right-align the panel under the anchor, then clamp it into the viewport.
 *
 * RIGHT-ALIGNED because that is what these panels did as `absolute right-0`
 * children and what every caller's layout was built around — the trigger is
 * usually the rightmost thing in a header row, so a left-aligned panel would
 * hang off the screen. CLAMPED because right-alignment alone does exactly that
 * for a trigger near either edge.
 */
export function placeAnchoredPanel(
  rect: AnchorRect,
  width: number,
  viewportWidth: number,
  viewportHeight: number,
): AnchoredPos {
  const left = Math.max(
    PANEL_EDGE,
    Math.min(rect.right - width, viewportWidth - width - PANEL_EDGE),
  );
  const below = viewportHeight - rect.bottom - PANEL_GAP - PANEL_EDGE;
  const above = rect.top - PANEL_GAP - PANEL_EDGE;

  // Drop down unless there is meaningfully more room above — a panel that flips
  // upward the moment the anchor passes the midpoint is a panel that jumps
  // around while you scroll.
  if (below >= MIN_DROP || below >= above) {
    return { left, top: rect.bottom + PANEL_GAP, maxHeight: below };
  }
  return { left, bottom: viewportHeight - rect.top + PANEL_GAP, maxHeight: above };
}
